import { OperationContext } from "../../sync-engine/OperationContext";
import { Operation } from "../../sync-engine/operations/Operation";
import type { WordSurfaceContext } from "@shared/lib/rpc/apis/word-add-in-api";

/**
 * Get document filename from URL using the Common API (works on all platforms).
 * Falls back to "Untitled" if the URL is not available.
 */
function getDocumentNameFromUrl(): Promise<string> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getFilePropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value.url) {
          // Extract filename from URL
          const url = result.value.url;
          const filename = url.split("/").pop() || "Untitled";
          // Decode URI components (e.g., %20 → space)
          const decodedName = decodeURIComponent(filename);
          resolve(decodedName || "Untitled");
        } else {
          resolve("Untitled");
        }
      });
    } catch {
      resolve("Untitled");
    }
  });
}

/**
 * Word SyncEngine Operation for reading the current surface context.
 *
 * This operation reads:
 * - Document title/name from document properties (Desktop) or URL (Word Online)
 * - Saved status from the document
 * - Currently selected text
 *
 * Usage:
 * ```typescript
 * const context = await syncEngine.perform(new GetSurfaceContextOperation());
 * console.log(context.documentName, context.isSaved, context.selectedText);
 * ```
 */
export class GetSurfaceContextOperation implements Operation<WordSurfaceContext> {
  async commit(context: OperationContext): Promise<WordSurfaceContext> {
    const wordContext = context.wordContext;
    const document = wordContext.document;

    // document.properties fails on Word Online even when WordApi 1.3 is "supported".
    // Skip properties access entirely on Word Online to avoid errors during sync.
    const platform = Office.context.diagnostics?.platform;
    const isWordOnline = platform === Office.PlatformType.OfficeOnline;
    const supportsProperties = !isWordOnline && Office.context.requirements.isSetSupported("WordApi", "1.3");

    let documentProperties: Word.DocumentProperties | null = null;
    if (supportsProperties) {
      documentProperties = document.properties;
      documentProperties.load("title");
    }

    // Load the saved status from the document
    document.load("saved");

    // Load the current selection
    const selection = document.getSelection();
    selection.load("text");

    // Sync to get the values
    await context.sync();

    // Get document name: use properties on Desktop, fallback to URL-based name
    let documentName = "Untitled";
    if (supportsProperties && documentProperties) {
      documentName = documentProperties.title || "Untitled";
    }
    // If no title from properties (Word Online or empty title), try URL-based fallback
    if (documentName === "Untitled") {
      documentName = await getDocumentNameFromUrl();
    }

    return {
      documentName,
      isSaved: document.saved,
      selectedText: selection.text || "",
    };
  }

  /**
   * GetSurfaceContextOperation does not merge with other operations.
   * Each request should get the fresh current state.
   */
  merge(_other: Operation<unknown>): Operation<WordSurfaceContext> | undefined {
    // We could merge consecutive operations since only the last result matters,
    // but for simplicity we don't merge.
    return undefined;
  }
}
